import { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { ProductSelectionMode, type ProductSelectionContext } from '../product';
import { ContactSelectionMode, type ContactSelectionContext } from '../contact';
import { StockSelectionMode, type StockSelectionContext } from '../stock';
import type { Product } from '../../types/product';
import type { Contact } from '../../types/contact';
import type { Stock } from '../../types/stock';
import type { ActivityType } from '../../types/activityType';
import type { ActivityFilters } from './types';
import { countActiveFilters } from './types';
import { activityTypeService } from '../../services/activityTypeService';
import { loadActivityFilters, saveActivityFilters } from '../../utils/activityFiltersStorage';

interface UseActivityFiltersParams {
  companyId: number | null;
  onFiltersChange?: () => void;
}

interface UseActivityFiltersReturn {
  filterData: {
    products: Product[];
    clients: Contact[];
    stocks: Stock[];
    providers: Contact[];
    activityTypes: ActivityType[];
    availableActivityTypes: ActivityType[];
    serialNumber: string;
    fromDate?: string;
    toDate?: string;
    showOnlyActive: boolean;
  };
  filterState: {
    showFilters: boolean;
    activeFiltersCount: number;
    loadingActivityTypes: boolean;
  };
  filterActions: {
    setShowFilters: (show: boolean) => void;
    handleSelectProducts: () => void;
    handleRemoveProduct: (productId: number) => void;
    handleSelectClients: () => void;
    handleRemoveClient: (clientId: number) => void;
    handleSelectStocks: () => void;
    handleRemoveStock: (stockId: number) => void;
    handleSelectProviders: () => void;
    handleRemoveProvider: (providerId: number) => void;
    handleActivityTypesChange: (activityTypes: ActivityType[]) => void;
    setSerialNumber: (sn: string) => void;
    setFromDate: (date: string | undefined) => void;
    setToDate: (date: string | undefined) => void;
    setShowOnlyActive: (value: boolean) => void;
    handleClearFilters: () => void;
  };
}

// Compares by entity ids (all the list request uses), with a fixed field order and defaults,
// so equivalent filters always serialize identically
function serializeFilters(filters: ActivityFilters): string {
  return JSON.stringify([
    filters.products.map(p => p.id),
    filters.clients.map(c => c.id),
    filters.stocks.map(s => s.id),
    filters.providers.map(p => p.id),
    filters.activityTypes.map(t => t.id),
    filters.serialNumber ?? '',
    filters.fromDate ?? null,
    filters.toDate ?? null,
    filters.showOnlyActive ?? true,
  ]);
}

export function useActivityFilters({
  companyId,
  onFiltersChange,
}: UseActivityFiltersParams): UseActivityFiltersReturn {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();

  // Filters from location.state (pickers, reports) win over the ones persisted in sessionStorage
  const [{ initialFilters, savedFilters }] = useState(() => {
    const saved = companyId ? loadActivityFilters(companyId) : undefined;
    const fromState = location.state?.activityFilters as ActivityFilters | undefined;
    return { initialFilters: fromState ?? saved, savedFilters: saved };
  });
  const initialProducts = initialFilters?.products || [];
  const initialClients = initialFilters?.clients || [];
  const initialStocks = initialFilters?.stocks || [];
  const initialProviders = initialFilters?.providers || [];
  const initialActivityTypes = initialFilters?.activityTypes || [];

  const [products, setProducts] = useState<Product[]>(initialProducts);
  const [clients, setClients] = useState<Contact[]>(initialClients);
  const [stocks, setStocks] = useState<Stock[]>(initialStocks);
  const [providers, setProviders] = useState<Contact[]>(initialProviders);
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>(initialActivityTypes);
  const [serialNumber, setSerialNumber] = useState<string>(initialFilters?.serialNumber ?? '');
  const [fromDate, setFromDate] = useState<string | undefined>(initialFilters?.fromDate);
  const [toDate, setToDate] = useState<string | undefined>(initialFilters?.toDate);
  const [showOnlyActive, setShowOnlyActive] = useState(initialFilters?.showOnlyActive ?? true);
  const [availableActivityTypes, setAvailableActivityTypes] = useState<ActivityType[]>([]);
  const [loadingActivityTypes, setLoadingActivityTypes] = useState(false);
  // Arrived with filters via a fresh navigation (confirmed picker selection, report drill-down),
  // as opposed to going back (POP) or coming from other pages
  const [arrivedWithFilters] = useState(
    () => navigationType === 'PUSH' && !!location.state?.activityFilters
  );
  const [showFilters, setShowFilters] = useState(arrivedWithFilters);

  // Load available activity types
  useEffect(() => {
    const loadActivityTypes = async () => {
      if (!companyId) return;
      
      setLoadingActivityTypes(true);
      try {
        const types = await activityTypeService.getAllByCompany(companyId);
        setAvailableActivityTypes(types.filter(t => !t.deactivatedDate));
      } catch (error) {
        console.error('Failed to load activity types:', error);
      } finally {
        setLoadingActivityTypes(false);
      }
    };

    loadActivityTypes();
  }, [companyId]);

  // Clear location state after consuming it to avoid re-applying on subsequent renders
  useEffect(() => {
    if (location.state?.activityFilters) {
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentFilters = useMemo<ActivityFilters>(
    () => ({ products, clients, stocks, providers, activityTypes, serialNumber, fromDate, toDate, showOnlyActive }),
    [products, clients, stocks, providers, activityTypes, serialNumber, fromDate, toDate, showOnlyActive]
  );
  const activeFiltersCount = useMemo(() => countActiveFilters(currentFilters), [currentFilters]);
  const serializedFilters = useMemo(() => serializeFilters(currentFilters), [currentFilters]);

  // Baseline for change detection is what was persisted, so a return with unchanged filters
  // (browser back, opening an activity) keeps the restored page. A fresh navigation with filters
  // has no baseline and always starts from the first page, even if the filters equal the saved ones.
  const lastSerializedFilters = useRef<string | null>(
    arrivedWithFilters ? null : savedFilters ? serializeFilters(savedFilters) : serializedFilters
  );

  useEffect(() => {
    if (companyId) {
      saveActivityFilters(companyId, currentFilters);
    }
    if (serializedFilters !== lastSerializedFilters.current) {
      lastSerializedFilters.current = serializedFilters;
      onFiltersChange?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serializedFilters, companyId]);

  const handleSelectProducts = () => {
    if (!companyId) return;

    const context: ProductSelectionContext = {
      mode: ProductSelectionMode.MULTIPLE_PRODUCT_SELECTION,
      returnPath: `/companies/${companyId}/activities`,
      activityFilters: currentFilters,
    };

    // Зберігаємо фільтри в поточному записі history, щоб кнопка "назад" їх відновила
    navigate(location.pathname, { replace: true, state: { activityFilters: currentFilters } });
    navigate(`/companies/${companyId}/products`, {
      state: context,
    });
  };

  const handleRemoveProduct = (productId: number) => {
    setProducts(prev => prev.filter(p => p.id !== productId));
  };

  const handleSelectClients = () => {
    if (!companyId) return;

    const context: ContactSelectionContext = {
      mode: ContactSelectionMode.MULTIPLE_CLIENT_SELECTION,
      returnPath: `/companies/${companyId}/activities`,
      activityFilters: currentFilters,
    };

    navigate(location.pathname, { replace: true, state: { activityFilters: currentFilters } });
    navigate(`/companies/${companyId}/contacts`, {
      state: context,
    });
  };

  const handleRemoveClient = (clientId: number) => {
    setClients(prev => prev.filter(c => c.id !== clientId));
  };

  const handleSelectStocks = () => {
    if (!companyId) return;

    const context: StockSelectionContext = {
      mode: StockSelectionMode.MULTIPLE_STOCK_SELECTION,
      returnPath: `/companies/${companyId}/activities`,
      activityFilters: currentFilters,
    };

    navigate(location.pathname, { replace: true, state: { activityFilters: currentFilters } });
    navigate(`/companies/${companyId}/stocks`, {
      state: context,
    });
  };

  const handleRemoveStock = (stockId: number) => {
    setStocks(prev => prev.filter(s => s.id !== stockId));
  };

  const handleSelectProviders = () => {
    if (!companyId) return;

    const context: ContactSelectionContext = {
      mode: ContactSelectionMode.MULTIPLE_PROVIDER_SELECTION,
      returnPath: `/companies/${companyId}/activities`,
      activityFilters: currentFilters,
    };

    navigate(location.pathname, { replace: true, state: { activityFilters: currentFilters } });
    navigate(`/companies/${companyId}/contacts`, {
      state: context,
    });
  };

  const handleRemoveProvider = (providerId: number) => {
    setProviders(prev => prev.filter(p => p.id !== providerId));
  };

  const handleActivityTypesChange = (newActivityTypes: ActivityType[]) => {
    setActivityTypes(newActivityTypes);
  };

  const handleClearFilters = () => {
    setProducts([]);
    setClients([]);
    setStocks([]);
    setProviders([]);
    setActivityTypes([]);
    setSerialNumber('');
    setFromDate(undefined);
    setToDate(undefined);
    setShowOnlyActive(true);
  };

  const filterData = useMemo(
    () => ({
      products,
      clients,
      stocks,
      providers,
      activityTypes,
      availableActivityTypes,
      serialNumber,
      fromDate,
      toDate,
      showOnlyActive,
    }),
    [products, clients, stocks, providers, activityTypes, availableActivityTypes, serialNumber, fromDate, toDate,
      showOnlyActive]
  );

  return {
    filterData,
    filterState: {
      showFilters,
      activeFiltersCount,
      loadingActivityTypes,
    },
    filterActions: {
      setShowFilters,
      handleSelectProducts,
      handleRemoveProduct,
      handleSelectClients,
      handleRemoveClient,
      handleSelectStocks,
      handleRemoveStock,
      handleSelectProviders,
      handleRemoveProvider,
      handleActivityTypesChange,
      setSerialNumber,
      setFromDate,
      setToDate,
      setShowOnlyActive,
      handleClearFilters,
    },
  };
}
